/**
 * Mobile sticky "Filter and sort" bar for collection/search pages.
 * Shows when the in-page facets UI scrolls off screen; hides again near the footer.
 */
class StickyFacetsBar extends HTMLElement {
  static FOOTER_HIDE_TRIGGER_ID = 'sticky-facets-hide-trigger';

  constructor() {
    super();
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDrawerToggle = this.onDrawerToggle.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onIntersect = this.onIntersect.bind(this);
    this.onFooterIntersect = this.onFooterIntersect.bind(this);
  }

  connectedCallback() {
    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.mediaQuery.addEventListener('change', this.onMediaChange);

    if (!this.mediaQuery.matches) return;

    this.init();
  }

  disconnectedCallback() {
    this.teardown();
    this.mediaQuery?.removeEventListener('change', this.onMediaChange);
  }

  teardown() {
    this.observer?.disconnect();
    this.footerObserver?.disconnect();
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.drawerDetails?.removeEventListener('toggle', this.onDrawerToggle);
    this.observer = null;
    this.footerObserver = null;
    this.sentinelInView = undefined;
    this.footerTriggerInView = undefined;
    this.initialized = false;
  }

  /**
   * Last mobile footer dropdown (#sticky-facets-hide-trigger).
   * Hides the sticky bar when it enters the viewport near the bottom of the page.
   */
  findFooterHideTrigger() {
    return document.getElementById(StickyFacetsBar.FOOTER_HIDE_TRIGGER_ID);
  }

  init() {
    if (this.initialized) return;

    // Original in-page facets block — bar visibility is tied to this leaving the viewport.
    this.sentinel = document.querySelector('.facets-container');
    if (!this.sentinel) {
      this.remove();
      return;
    }

    this.trigger = this.querySelector('.sticky-facets-bar__trigger');
    this.trigger?.addEventListener('click', this.onTriggerClick);

    this.drawerDetails = document.querySelector('.mobile-facets__disclosure');
    this.drawerDetails?.addEventListener('toggle', this.onDrawerToggle);

    // Opening the drawer from the sticky bar must not scroll the page to the summary.
    const summary = this.drawerDetails?.querySelector('summary');
    if (summary) {
      const nativeFocus = summary.focus.bind(summary);
      summary.focus = (options) => nativeFocus({ preventScroll: true, ...options });
    }

    this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0 });
    this.observer.observe(this.sentinel);

    const footerHideTrigger = this.findFooterHideTrigger();
    if (footerHideTrigger) {
      this.footerObserver = new IntersectionObserver(this.onFooterIntersect, { threshold: 0 });
      this.footerObserver.observe(footerHideTrigger);
    }

    this.initialized = true;
    this.removeAttribute('hidden');
    StickyFacetsBar.syncFromFacets();
  }

  onMediaChange(event) {
    if (event.matches) {
      this.init();
    } else {
      this.teardown();
      this.toggleVisible(false);
    }
  }

  onIntersect(entries) {
    this.sentinelInView = entries[0]?.isIntersecting;
    this.updateVisibility();
  }

  onFooterIntersect(entries) {
    this.footerTriggerInView = entries[0]?.isIntersecting;
    this.updateVisibility();
  }

  onTriggerClick(event) {
    event.preventDefault();
    this.openFacetsFromSticky();
  }

  /**
   * Opens the existing mobile facets drawer without jumping scroll position.
   * The drawer lives in the page markup; the sticky bar is only a remote trigger.
   */
  openFacetsFromSticky() {
    const menuDrawer = document.querySelector('menu-drawer.mobile-facets__wrapper');
    const details = this.drawerDetails;
    const summary = details?.querySelector('summary');

    if (!menuDrawer || !details || !summary || details.open) return;

    this.scrollAnchor = window.scrollY;
    this.openedFromSticky = true;
    this.pendingScrollToTop = false;

    details.setAttribute('open', '');
    menuDrawer.openMenuDrawer(summary);

    if (window.matchMedia('(max-width: 990px)').matches) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    this.enforceScrollPosition(this.scrollAnchor);
  }

  /**
   * Briefly re-applies scroll position while the drawer animates open.
   * Shopify's menu-drawer can nudge the page when focus moves to the summary.
   */
  enforceScrollPosition(scrollY, duration = 400) {
    const lockId = ++this._scrollLockId;
    const start = performance.now();
    const restore = () => {
      if (lockId !== this._scrollLockId || this.pendingScrollToTop) return;
      if (window.scrollY !== scrollY) {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      }
    };

    restore();
    requestAnimationFrame(restore);

    const lock = (time) => {
      if (lockId !== this._scrollLockId || this.pendingScrollToTop) return;
      restore();
      if (time - start < duration) {
        requestAnimationFrame(lock);
      }
    };

    requestAnimationFrame(lock);
  }

  scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  static clearScrollLock() {
    const bar = document.querySelector('sticky-facets-bar');
    if (!bar) return;
    bar._scrollLockId = (bar._scrollLockId || 0) + 1;
    bar.openedFromSticky = false;
  }

  /** Called after facet AJAX updates so the product grid returns to the top on mobile. */
  static scrollToCollectionTop() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const bar = document.querySelector('sticky-facets-bar');
    if (!bar) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return;
    }

    bar.pendingScrollToTop = true;
    StickyFacetsBar.clearScrollLock();

    const scrollTop = () => bar.scrollToTop();
    scrollTop();
    requestAnimationFrame(scrollTop);
    [0, 50, 150, 300].forEach((delay) => setTimeout(scrollTop, delay));

    requestAnimationFrame(() => bar.updateVisibility());
  }

  onDrawerToggle() {
    const drawerOpen = this.drawerDetails?.open;
    this.classList.toggle('sticky-facets-bar--drawer-open', drawerOpen);

    if (drawerOpen) {
      if (this.openedFromSticky && !this.pendingScrollToTop) {
        this.enforceScrollPosition(this.scrollAnchor);
      }
      return;
    }

    if (this.pendingScrollToTop) {
      this.pendingScrollToTop = false;
      this.openedFromSticky = false;
      this._scrollLockId = (this._scrollLockId || 0) + 1;
      this.scrollToTop();
      this.updateVisibility();
      return;
    }

    if (this.openedFromSticky) {
      this.enforceScrollPosition(this.scrollAnchor, 200);
      this.openedFromSticky = false;
    }

    this.updateVisibility();
  }

  /**
   * Bar is visible only when facets have scrolled away and the footer has not
   * yet reached the viewport (#sticky-facets-hide-trigger on the last footer dropdown).
   */
  updateVisibility() {
    const sentinelVisible =
      typeof this.sentinelInView === 'boolean'
        ? this.sentinelInView
        : this.sentinel?.getBoundingClientRect().bottom > 0 &&
        this.sentinel.getBoundingClientRect().top < window.innerHeight;
    const footerTriggerVisible = this.footerTriggerInView === true;
    this.toggleVisible(!sentinelVisible && !footerTriggerVisible);
  }

  toggleVisible(show) {
    this.classList.toggle('sticky-facets-bar--visible', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  /** Keeps sticky bar counts/badge in sync after facet filtering or section reload. */
  static syncFromFacets() {
    const bar = document.querySelector('sticky-facets-bar');
    if (!bar) return;

    const sourceCount = document.getElementById('ProductCount');
    const targetCount = document.getElementById('StickyFacetsProductCount');
    if (sourceCount && targetCount) {
      targetCount.innerHTML = sourceCount.innerHTML;
    }

    const activeFilters = document.querySelectorAll(
      '.active-facets-mobile > facet-remove:not(.active-facets__button-wrapper)'
    );
    const count = activeFilters.length;
    const badge = bar.querySelector('.sticky-facets-bar__badge');

    if (badge) {
      badge.textContent = count;
      badge.classList.toggle('hidden', count === 0);
    }

    bar.dataset.activeCount = count;
  }
}

customElements.define('sticky-facets-bar', StickyFacetsBar);

// Theme editor: re-sync counts when the collection/search section is re-rendered.
document.addEventListener('shopify:section:load', () => {
  StickyFacetsBar.syncFromFacets();
});
